import { Alert, Checkbox, Table } from 'antd';
import classNames from 'classnames';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { alertActions } from '../../../actions/alert.actions';
import { history } from '../../../helpers/history';
import documentEditMode from '../constants/documentEditMode';
import { documentsConstants } from '../constants/documents.constants';
import useSorter from '../hooks/useSorter';
import documentListActions from '../store/actions/document-list.actions';
import editDocumentActions from '../store/actions/edit-document.actions';
import ExpandedRow from './ExpandedRow';
import PaginationBlock from './PaginationBlock';
import { getColumns } from './utils/getColumns';
import { setCurrent } from './utils/setCurrent';
import usePagination from '../../../hooks/usePagination';
import styles from './table.module.scss';
import useCompanies from '../../../queries/useCompanies';
import { filterNewConst } from '../constants/documentFilterNew.constantas';
import { documentsActions } from '../store/actions/documents.actions';

let dspxHoveredRowID = null;

const RowRenderer = ({
  record,
  expanded,
  expandedTableRowRender,
  ...props
}) => {
  const isHovered = dspxHoveredRowID === record.id;
  if (isHovered) {
    props.className += ' current';
  }

  if (expanded) {
    props.className += ' expanded'
  }
  const { onClick: _onExpandedRowClick, ...trProps } = props;
  return (
    <>
      <tr {...props} key={record.id} />
      {expanded && expandedTableRowRender(record, trProps)}
    </>
  );
};

const DocumentsTable = memo(() => {
  const [expandedRows, setExpandedRows] = useState({});
  const [tableSorter, setTableSorter] = useState();
  const [viewedDocumentsTemp, setViewedDocumentsTemp] = useState([]);

  const postDocumentID = useSelector(
    (store) => store.newDocumentStore.postDocumentID
  );
	const documents = useSelector((store) => store.documentsStore.documents);
  const goResort = useSelector((store) => store.documentsStore.goResort);
  const selectedDocuments = useSelector(
    (store) => store.documentListStore.selectedDocuments
  );
  const pageSize = useSelector((store) => store.documentListStore.pageSize);
  const { companyId} = useSelector(
      (state) => state.documentFilterNewStore
    );

  const dispatch = useDispatch();

  const { data: companies = [] } = useCompanies({
    staleTime: Infinity,
  });

  const defaultTableSorter = {
    column: {
      key: 'createDate',
      defaultSortOrder: 'descend',
    },
    order: 'descend',
    columnKey: 'createDate',
  };

  const [sortParam, sortOrder, sorter] = useSorter(
    tableSorter || defaultTableSorter,
    documents
  );

  const [
    currentPageNumber,
    setCurrentPageNumber,
    maxPage,
    shownDocuments,
    firstIndexOnPage,
  ] = usePagination(documents, pageSize);

  const dataSource = useMemo(() => {
    return postDocumentID
      ? documents.filter(({ id }) => String(postDocumentID) === String(id))
      : shownDocuments;
  }, [documents, postDocumentID, shownDocuments]);

  useEffect(() => {
    history.listen(() => {
      dispatch(alertActions.clear());
    });
  }, []);

  useEffect(() => {
    if (postDocumentID) {
      setCurrentPageNumber(0);
      setExpandedRows((expandedRows) => ({
        ...expandedRows,
        [postDocumentID]: true,
      }));
    }
  }, [postDocumentID]);

  const handleEditNumberDateClick = useCallback((e, v) => {
    e.stopPropagation();
    dispatch(
      editDocumentActions.openModal({
        ...v,
        editMode: documentEditMode.NUMBER_DATE,
      })
    );
  }, []);

  const markViewed = (rid) => {
    setViewedDocumentsTemp([...viewedDocumentsTemp, rid]);
  };

  const onTableChange = (_pagination, _filters, sorter) => {
    setTableSorter(sorter);
  };

	const checkViewed = (docId) => {
		const viewedDocument = viewedDocumentsTemp.filter((id) => id === docId);
		return viewedDocument.length > 0;
	};
	const onTableRow = (record) => ({
    id: 'dspxRow' + record.id,
		style: {
			borderLeft: record.isViewed || checkViewed(record.id) ? 'unset' : '10px solid #5db6ee',
		},
    onMouseLeave: () => {
      const row = document.getElementById('dspxRow' + record.id);
      const expandedRowsOfOne = document.getElementsByClassName(
        'dspxExRow' + record.id
      );
      setCurrent(row, false);
      const expandedRowsOfOneArray = [...expandedRowsOfOne];
      expandedRowsOfOneArray.forEach((expandedRow) => {
        setCurrent(expandedRow, false);
      });

      dspxHoveredRowID = null;
    },

    onMouseEnter: () => {
      const row = document.getElementById('dspxRow' + record.id);
      const expandedRowsOfOne = document.getElementsByClassName(
        'dspxExRow' + record.id
      );
      setCurrent(row);
      const expandedRowsOfOneArray = [...expandedRowsOfOne];
      expandedRowsOfOneArray.forEach((expandedRow) => {
        setCurrent(expandedRow);
      });

      if (dspxHoveredRowID) {
        const prevRow = document.getElementById('dspxRow' + record.id);
        const prevExpandedRowsOfOne = document.getElementsByClassName(
          'dspxExRow' + record.id
        );

        setCurrent(prevRow, false);
        const prevExpandedRowsOfOneArray = [...prevExpandedRowsOfOne];
        prevExpandedRowsOfOneArray.forEach((prevExpandedRow) => {
          setCurrent(prevExpandedRow, false);
        });
      }

      dspxHoveredRowID = record.id;
    },
  });

  const tableSelectionChange = (selectedRowKeys) => {
    if (goResort) dispatch({ type: documentsConstants.UNSET_RESORT });

    dispatch(documentListActions.setSelected(selectedRowKeys));
  };

  const onCheckAllChange = useCallback(
    (e) => {
      if (goResort) {
        dispatch({ type: documentsConstants.UNSET_RESORT });
      }
      const ids = e.target.checked ? documents.map((el) => el.id) : [];
      dispatch(documentListActions.setSelected(ids));
    },
    [goResort]
  );

  const rowSelection = {
    selectedRowKeys: selectedDocuments,
    onChange: tableSelectionChange,
    columnWidth: '46px',
    columnTitle: (
      <Checkbox
        indeterminate={
          !!selectedDocuments.length &&
          selectedDocuments.length < documents.length
        }
        onChange={onCheckAllChange}
        checked={selectedDocuments.length === documents.length}
      />
    ),
  };

  if (!Array.isArray(documents))
    return <Alert message={documents} type="error" showIcon />;

  if (goResort) {
    documents.sort(sorter);
  }
  const defaultExpandedRowRender = useCallback(() => null, []);

  const onExpand = useCallback(
    (expanded, record) => {
			expanded && !record?.isViewed && markViewed(record.id);
      setExpandedRows((expandedRows) => ({
        ...expandedRows,
        [record.id]: expanded,
      }));
    },
    [markViewed]
  );

  const onRow = useCallback(
    (record) => {
      const expanded = !!expandedRows[record.id];
      return {
        ...onTableRow(record),
        record,
        expanded,
        expandedTableRowRender: (record, trProps) => (
          <ExpandedRow
            rowOpened={expandedRows[record.id]}
            record={record}
            trProps={trProps}
          />
        ),
      };
    },
    [expandedRows, onTableRow]
  );
  const onHeaderRow = useCallback(() => {
    return {
      style: { whiteSpace: 'nowrap' },
    };
  });

  const components = useMemo(
    () => ({
      body: {
        row: RowRenderer,
      },
    }),
    [RowRenderer]
  );

  const columns = useMemo(() => getColumns(
    sortParam, 
    sortOrder, 
    handleEditNumberDateClick, 
    expandedRows,
    setTableSorter
  ), [sortParam, sortOrder, handleEditNumberDateClick, expandedRows]);

   useEffect(() => {
    if(companies?.length === 1 && !companyId) {
      dispatch({
      type: filterNewConst.APPLY_COMPANY_ID,
      payload: companies[0].id,
    });

    dispatch(documentsActions.fetchDocuments());
    }
   }, [
    companies,
    companyId
   ])

  return (
    <div className={styles.tableContainer} id='docTableContainer'>
      <Table
        tableLayout="initial"
        size="middle"
        className={classNames('dspxDocumentsTable', styles.table)}
        rowKey="id"
        defaultExpandAllRows={false}
        rowSelection={rowSelection}
        expandRowByClick={true}
        onChange={onTableChange}
        onExpand={onExpand}
        expandedRowRender={defaultExpandedRowRender}
        pagination={false}
        columns={columns}
        onRow={onRow}
        onHeaderRow={onHeaderRow}
        components={components}
        dataSource={dataSource}
        expandIconAsCell={false}
      />
      {!postDocumentID && <PaginationBlock
        lastIndexOnPage={firstIndexOnPage + shownDocuments.length}
        firstIndexOnPage={firstIndexOnPage}
        currentPageNumber={currentPageNumber}
        maxPage={maxPage}
        setPageNumber={(page) => setCurrentPageNumber(page - 1)}
        selectedDocumentsAmount={selectedDocuments.length}
      />}
    </div>
  );
});

export default DocumentsTable;
